// src/components/Sidebar.js
import React, { useContext, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import {
  FaChevronDown, FaChevronRight, FaAngleDoubleLeft, FaAngleDoubleRight,
  FaHome, FaUsers, FaDollarSign, FaUserPlus, FaBookOpen, FaCog, FaChild,
  FaCheckSquare, FaCalendarAlt, FaFileAlt, FaClipboardList, FaLayerGroup, FaCircle,
  FaCalendarCheck, FaChartBar, FaGlobeAmericas, FaTimes,
} from "react-icons/fa";

const ICONOS = {
  home: FaHome,
  users: FaUsers,
  "dollar-sign": FaDollarSign,
  "user-plus": FaUserPlus,
  "book-open": FaBookOpen,
  settings: FaCog,
  child: FaChild,
  "check-square": FaCheckSquare,
  calendar: FaCalendarAlt,
  "file-text": FaFileAlt,
  "clipboard-list": FaClipboardList,
  layers: FaLayerGroup,
  "calendar-check": FaCalendarCheck,
  "chart-bar": FaChartBar,
  globe: FaGlobeAmericas,
};

const obtenerIcono = (nombreIcono) => ICONOS[nombreIcono] || FaCircle;

const getRuta = (ruta) => {
  if (!ruta) return "/dashboard";
  return ruta === "/dashboard" ? "/dashboard" : `/dashboard${ruta}`;
};

const tieneSubmoduloActivo = (modulo, pathname) =>
  (modulo.submodulos || []).some((sub) => getRuta(sub.ruta) === pathname);

export default function Sidebar({ abiertoMovil = false, onCerrarMovil = () => {} }) {
  const { modulos } = useContext(AuthContext);
  const location = useLocation();
  const [abierto, setAbierto] = useState({});
  const [colapsado, setColapsado] = useState(() => {
    try {
      return localStorage.getItem("sidebarColapsado") === "true";
    } catch {
      return false;
    }
  });

  const toggle = (id) => {
    setAbierto((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleColapsado = () => {
    setColapsado((prev) => {
      const nuevo = !prev;
      try {
        localStorage.setItem("sidebarColapsado", String(nuevo));
      } catch {
        // localStorage no disponible; la preferencia solo dura la sesión
      }
      return nuevo;
    });
  };

  const cerrarSiMovil = () => {
    if (window.innerWidth < 768) onCerrarMovil();
  };

  return (
    <>
      {/* Fondo oscuro detrás del menú, solo visible en móvil cuando está abierto */}
      {abiertoMovil && (
        <div
          className="fixed inset-0 bg-slate-900/50 z-40 md:hidden"
          onClick={onCerrarMovil}
          aria-hidden="true"
        />
      )}

      <aside
        className={`
          fixed inset-y-0 left-0 z-50 h-screen bg-white text-slate-600 border-r border-slate-200 flex flex-col
          transition-transform duration-200 w-64
          ${abiertoMovil ? "translate-x-0" : "-translate-x-full"}
          md:translate-x-0 md:static md:z-auto md:transition-all
          ${colapsado ? "md:w-[72px]" : "md:w-64"} md:shrink-0
        `}
      >

      {/* LOGO */}
      <div className={`flex items-center gap-3 border-b border-slate-200 py-4 ${colapsado ? "md:justify-center md:px-2" : ""} px-6`}>
        <img
          src="/logoIBFMO.png"
          alt="Logo Iglesia"
          className="h-9 w-9 object-contain rounded-full bg-slate-100 p-0.5 shrink-0"
        />
        {!colapsado && (
          <span className="text-base font-semibold leading-tight text-slate-800 tracking-wide truncate flex-1">
            Monte de los Olivos
          </span>
        )}
        <button
          type="button"
          onClick={onCerrarMovil}
          aria-label="Cerrar menú"
          className="md:hidden p-1 text-slate-400 hover:text-slate-700"
        >
          <FaTimes />
        </button>
      </div>

      {/* NAVEGACIÓN */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-3">

        {modulos.map((modulo) => {
          const id = modulo.id_modulo || modulo.nombre;
          const tieneSubmodulos = modulo.submodulos && modulo.submodulos.length > 0;
          const ruta = getRuta(modulo.ruta);
          const activo = location.pathname === ruta;
          const expandido = abierto[id] ?? tieneSubmoduloActivo(modulo, location.pathname);
          const Icono = obtenerIcono(modulo.icono);

          return (
            <div key={id} className="relative group/item">

              {tieneSubmodulos ? (
                <div
                  className={`flex items-center border-l-4 transition ${
                    activo
                      ? "border-indigo-600 bg-indigo-50 text-indigo-700"
                      : "border-transparent text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                >
                  <Link
                    to={ruta}
                    title={colapsado ? modulo.nombre : undefined}
                    onClick={cerrarSiMovil}
                    className={`flex-1 flex items-center gap-3 py-2.5 text-sm font-medium truncate ${
                      colapsado ? "justify-center px-0" : "pl-5 pr-2"
                    }`}
                  >
                    <Icono className="text-base shrink-0" />
                    {!colapsado && <span className="truncate">{modulo.nombre}</span>}
                  </Link>
                  {!colapsado && (
                    <button
                      onClick={() => toggle(id)}
                      aria-label={expandido ? "Contraer" : "Expandir"}
                      className="pr-4 pl-2 py-2.5 opacity-60 hover:opacity-100"
                    >
                      {expandido ? (
                        <FaChevronDown className="text-xs" />
                      ) : (
                        <FaChevronRight className="text-xs" />
                      )}
                    </button>
                  )}
                </div>
              ) : (
                <Link
                  to={ruta}
                  title={colapsado ? modulo.nombre : undefined}
                  onClick={cerrarSiMovil}
                  className={`flex items-center gap-3 py-2.5 border-l-4 transition text-sm font-medium ${
                    colapsado ? "justify-center px-0" : "pl-5 pr-4"
                  } ${
                    activo
                      ? "border-indigo-600 bg-indigo-50 text-indigo-700"
                      : "border-transparent text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                >
                  <Icono className="text-base shrink-0" />
                  {!colapsado && <span className="truncate">{modulo.nombre}</span>}
                </Link>
              )}

              {/* Submenú expandido (modo normal) */}
              {tieneSubmodulos && !colapsado && expandido && (
                <div className="bg-slate-50">
                  {modulo.submodulos.map((sub) => {
                    const subRuta = getRuta(sub.ruta);
                    const subActivo = location.pathname === subRuta;

                    return (
                      <Link
                        key={sub.id_modulo || sub.nombre}
                        to={subRuta}
                        onClick={cerrarSiMovil}
                        className={`block pl-9 pr-4 py-2 border-l-4 text-sm transition ${
                          subActivo
                            ? "border-indigo-600 bg-indigo-50 text-indigo-700"
                            : "border-transparent text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                        }`}
                      >
                        {sub.nombre}
                      </Link>
                    );
                  })}
                </div>
              )}

              {/* Flyout con submódulos (modo colapsado, aparece al pasar el mouse) */}
              {tieneSubmodulos && colapsado && (
                <div className="absolute left-full top-0 ml-1 hidden group-hover/item:block z-50 min-w-[200px] bg-white border border-slate-200 rounded-lg shadow-xl py-2">
                  <p className="px-4 py-1 text-xs font-semibold text-slate-400 uppercase">{modulo.nombre}</p>
                  {modulo.submodulos.map((sub) => {
                    const subRuta = getRuta(sub.ruta);
                    const subActivo = location.pathname === subRuta;

                    return (
                      <Link
                        key={sub.id_modulo || sub.nombre}
                        to={subRuta}
                        className={`block px-4 py-2 text-sm transition ${
                          subActivo ? "text-indigo-700 bg-indigo-50" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                        }`}
                      >
                        {sub.nombre}
                      </Link>
                    );
                  })}
                </div>
              )}

            </div>
          );
        })}

      </nav>

      {/* COLAPSAR MENÚ (solo aplica en escritorio) */}
      <div className="hidden md:block border-t border-slate-200 p-2 shrink-0">
        <button
          onClick={toggleColapsado}
          title={colapsado ? "Expandir menú" : "Colapsar menú"}
          className={`w-full flex items-center gap-3 py-2 rounded-md text-slate-500 hover:bg-slate-50 hover:text-slate-900 transition text-sm ${
            colapsado ? "justify-center px-0" : "px-3"
          }`}
        >
          {colapsado ? <FaAngleDoubleRight /> : <FaAngleDoubleLeft />}
          {!colapsado && <span>Colapsar menú</span>}
        </button>
      </div>

      </aside>
    </>
  );
}
